/**
 * Agent hotel — SSOT יחיד למחיר לקוח: supplierNet + hotel_hr_commission_percentage.
 * displayCurrencies.ils / BE ourPrice.PWCCF = supplier quote only — never customer final.
 */

import {
  formatHotelSearchAmountForDisplay,
  normalizeHotelSearchCurrencyCode,
} from "../../constants/hotelCurrencies";
import { formatHotelIlsRoundedDisplay } from "../formatHotelIlsRoundedDisplay";
import { applyFXSettlement, roundMoney2 } from "../../pricing/pricingEngine";
import { computeAgentHotelFlowPriceVm } from "../agentHotelSearchResultsPricing";
import {
  formatResolvedAgentHotelPriceLine,
  resolveAgentHotelSupplierPriceIls,
} from "./agentHotelPricingPipeline";
import {
  getHotelProfilePackageSupplierUsd,
  pickHotelPackageOrderOurPrice,
  resolveHotelProfilePackageRawCurrency,
  resolveHotelProfileUiDisplayCurrency,
  resolveProfileDisplayPrice,
} from "../hotelSellingPrice";

/** @param {object|null|undefined} profilePriceContext */
export function isAgentHotelCustomerFacingPricingEnabled(profilePriceContext) {
  const ap = profilePriceContext?.agentHotelPricing;
  return Boolean(ap?.enabled && Number.isFinite(Number(ap?.hotelHrCommissionPercentage)));
}

function resolveDisplayCurrency(profilePriceContext) {
  return (
    resolveHotelProfileUiDisplayCurrency(profilePriceContext) ??
    normalizeHotelSearchCurrencyCode(profilePriceContext?.selectedHotelCurrency) ??
    "ILS"
  );
}

/**
 * @param {import("./agentHotelPricingPipeline").resolveAgentHotelPricing extends Function ? ReturnType<import("./agentHotelPricingPipeline").resolveAgentHotelPricing> : object} vm
 * @param {string|null|undefined} settlementCurrency
 */
export function buildAgentHotelOurPriceFromCustomerFacingVm(vm, settlementCurrency = "ILS") {
  if (!vm || vm.pricingStatus !== "ok" || vm.finalPrice == null) return null;
  const supplierNet = vm.basePrice != null ? Number(vm.basePrice) : null;
  const hrCommission =
    vm.commissionAmount != null && Number.isFinite(Number(vm.commissionAmount))
      ? Number(vm.commissionAmount)
      : 0;
  const priceWithCommission = Number(vm.finalPrice);
  const cur = settlementCurrency ?? vm.displayCurrency ?? "ILS";
  return {
    initialPrice: supplierNet ?? priceWithCommission,
    supplierNet: supplierNet ?? priceWithCommission,
    hrCommissionPercent: vm.commissionPercent ?? 0,
    hrCommission,
    agentCommission: 0,
    agentCommissionPercent: 0,
    handlingFee: 0,
    priceWithCommission,
    creditCardFee: 0,
    pricePreFinalRound: priceWithCommission,
    priceWithCommissionAndCreditCardFee: priceWithCommission,
    originalCurrency: cur,
    pricingSource: "agent_hotel_customer_facing_ssot",
    unifiedHrMarginPercent: vm.commissionPercent ?? 0,
    unifiedHrMargin: hrCommission,
  };
}

/**
 * @param {object} params
 * @param {number|null|undefined} [params.supplierAmount]
 * @param {string|null|undefined} [params.supplierCurrency]
 * @param {object|null|undefined} [params.pkg]
 * @param {object|null|undefined} [params.hotelInfo]
 * @param {object|null|undefined} params.profilePriceContext
 * @param {string} [params.pricingModel]
 * @returns {object}
 */
export function resolveAgentHotelCustomerFacingPriceIls({
  supplierAmount = null,
  supplierCurrency = null,
  pkg = null,
  hotelInfo = null,
  profilePriceContext = null,
  pricingModel = "agent_hotel_customer_facing",
  supplierDecision = null,
}) {
  const ap = profilePriceContext?.agentHotelPricing;
  const baseFail = {
    supplierNet: null,
    hrCommissionPercent: null,
    hrCommission: null,
    priceWithCommission: null,
    bookingSsotIls: null,
    displayLine: null,
    displayCurrency: null,
    ourPrice: null,
    vm: null,
    pricingStatus: "not_agent_hr",
    reason: "not_agent_hr",
    pipeline: null,
  };

  if (!isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    return { ...baseFail, reason: "agent_hotel_pricing_disabled_or_missing_entitlement" };
  }

  const hasDecision = supplierDecision && typeof supplierDecision === "object";
  const decisionSupplierAmount =
    hasDecision &&
    Number.isFinite(Number(supplierDecision.chosenSupplierTotalUsd)) &&
    Number(supplierDecision.chosenSupplierTotalUsd) > 0
      ? Number(supplierDecision.chosenSupplierTotalUsd)
      : null;
  const decisionSupplierSource = hasDecision
    ? supplierDecision.chosenSupplierTotalSource ?? null
    : null;
  const rawSupplier =
    decisionSupplierAmount != null
      ? decisionSupplierAmount
      : supplierAmount != null && Number.isFinite(Number(supplierAmount)) && Number(supplierAmount) > 0
        ? Number(supplierAmount)
        : pkg
          ? getHotelProfilePackageSupplierUsd(pkg)
          : null;

  if (rawSupplier == null) {
    return { ...baseFail, pricingStatus: "invalid_api_price", reason: "missing_supplier_amount" };
  }

  const rawCur =
    supplierCurrency ??
    (hasDecision ? supplierDecision.supplierCurrency ?? null : null) ??
    (pkg
      ? resolveHotelProfilePackageRawCurrency(pkg, hotelInfo, profilePriceContext)
      : null);
  const displayCur = resolveDisplayCurrency(profilePriceContext);

  const core = resolveProfileDisplayPrice({
    rawAmount: rawSupplier,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "resolveAgentHotelCustomerFacingPriceIls",
    hotelId: profilePriceContext?.hotelId ?? null,
    packageId: pkg?.PackageId ?? pkg?.packageID ?? pkg?.packageId ?? null,
    sourcePath: "supplier→agent_hr_customer_facing",
    profilePriceContext,
  });

  if (core.rawCurrencyMissing || core.fxUnavailable || core.convertedAmount == null) {
    return {
      ...baseFail,
      supplierNet: rawSupplier,
      pricingStatus: core.pricingStatus ?? "fx_unavailable",
      reason: core.reason ?? core.pricingStatus ?? "fx_unavailable",
      pipeline: "agent_hr_customer_facing:fx_failed",
      chosenSupplierTotalSource: decisionSupplierSource,
      chosenSupplierTotalUsd: decisionSupplierAmount,
      supplierNetIlsInput: core.convertedAmount ?? null,
      supplierNetIlsOutput: null,
      usedDisplayCurrenciesIls: false,
      usedPackagePriceFinalPrice: !hasDecision && supplierAmount == null,
      usedRoomsSumFinalPrice: decisionSupplierSource === "rooms_sum",
      fallbackReason: core.reason ?? core.pricingStatus ?? "fx_unavailable",
    };
  }

  const vm = computeAgentHotelFlowPriceVm({
    apiPrice: core.convertedAmount,
    transactionCurrency: displayCur,
    hotelSearchResultsCurrency: displayCur,
    hotelSearchListDisplayCurrency: displayCur,
    hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
    fixedHotelCommission: ap.fixedHotelCommission,
    supplierPriceIls: resolveAgentHotelSupplierPriceIls({
      basePrice: rawSupplier,
      flowCurrency: rawCur,
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    }),
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    pricingAuditMeta: profilePriceContext?.hotelProfilePricingAuditMeta
      ? {
          ...profilePriceContext.hotelProfilePricingAuditMeta,
          pricingModel,
        }
      : { pricingModel },
  });

  if (vm.pricingStatus !== "ok" || vm.finalPrice == null) {
    return {
      ...baseFail,
      supplierNet: core.convertedAmount,
      displayCurrency: displayCur,
      vm,
      pricingStatus: vm.pricingStatus ?? "agent_hr_reject",
      reason: vm.reason ?? vm.pricingStatus,
      pipeline: "agent_hr_customer_facing:vm_not_ok",
    };
  }

  const priceWithCommission = Number(vm.finalPrice);
  const supplierIlsForBooking = resolveAgentHotelSupplierPriceIls({
    basePrice: rawSupplier,
    flowCurrency: rawCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
  });
  const bookingSsotIls =
    displayCur === "ILS"
      ? priceWithCommission
      : supplierIlsForBooking != null && vm.commissionIls != null
        ? roundMoney2(supplierIlsForBooking + Number(vm.commissionIls))
        : roundMoney2(priceWithCommission);

  const ourPrice = buildAgentHotelOurPriceFromCustomerFacingVm(vm, displayCur);

  return {
    supplierNet: vm.basePrice != null ? Number(vm.basePrice) : core.convertedAmount,
    hrCommissionPercent: vm.commissionPercent ?? 0,
    hrCommission:
      vm.commissionAmount != null && Number.isFinite(Number(vm.commissionAmount))
        ? Number(vm.commissionAmount)
        : 0,
    priceWithCommission,
    bookingSsotIls,
    displayLine: formatResolvedAgentHotelPriceLine(vm),
    displayCurrency: displayCur,
    ourPrice,
    vm,
    pricingStatus: "ok",
    reason: null,
    pipeline: "agent_hr_customer_facing_ssot",
    rawSupplierAmount: rawSupplier,
    rawSupplierCurrency: rawCur,
    convertedSupplierAmount: core.convertedAmount,
    chosenSupplierTotalSource: decisionSupplierSource,
    chosenSupplierTotalUsd: decisionSupplierAmount ?? rawSupplier,
    supplierNetIlsInput: core.convertedAmount,
    supplierNetIlsOutput:
      vm.basePrice != null ? Number(vm.basePrice) : core.convertedAmount,
    usedDisplayCurrenciesIls: false,
    usedPackagePriceFinalPrice: !hasDecision && supplierAmount == null,
    usedRoomsSumFinalPrice: decisionSupplierSource === "rooms_sum",
    fallbackReason: null,
  };
}

/**
 * Secondary display line for agent HR customer-facing cells — same final-price SSOT as primary.
 * USD primary → ILS final in parens; ILS primary → no secondary (never supplier pre-HR USD).
 *
 * @param {ReturnType<typeof resolveAgentHotelCustomerFacingPriceIls>|null|undefined} cf
 * @param {object|null|undefined} profilePriceContext
 * @returns {{ line: string|null, source: string|null }}
 */
export function resolveAgentHotelCustomerFacingSecondaryDisplayLine(
  cf,
  profilePriceContext = null
) {
  if (!cf || cf.pricingStatus !== "ok" || cf.priceWithCommission == null) {
    return { line: null, source: null };
  }

  const displayCur = String(cf.displayCurrency ?? "ILS")
    .trim()
    .toUpperCase();
  const finalAmount = Number(cf.priceWithCommission);
  if (!Number.isFinite(finalAmount) || finalAmount <= 0) {
    return { line: null, source: null };
  }

  if (displayCur === "USD") {
    const fx = applyFXSettlement({
      amount: finalAmount,
      fromCurrency: "USD",
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    const converted = fx?.convertedAmount;
    if (converted == null || !Number.isFinite(Number(converted)) || Number(converted) <= 0) {
      return { line: null, source: null };
    }
    const formattedIls = formatHotelIlsRoundedDisplay(Math.ceil(Number(converted)));
    if (!formattedIls) {
      return { line: null, source: null };
    }
    return {
      line: `(${formattedIls})`,
      source: "agent_hr_customer_facing_final_ils_secondary",
    };
  }

  return { line: null, source: null };
}

/**
 * Search results row — same SSOT as profile/booking.
 */
export function resolveAgentHotelCustomerFacingPriceFromSearchRow({
  apiPrice,
  resultsDatasetCurrency,
  displayCurrency,
  hotelHrCommissionPercentage,
  fixedHotelCommission = 0,
  pricingAuditMeta = null,
  currencyRate = null,
  usdToIls = null,
  ilsToUsd = null,
}) {
  const vm = computeAgentHotelFlowPriceVm({
    apiPrice,
    transactionCurrency: resultsDatasetCurrency,
    hotelSearchResultsCurrency: resultsDatasetCurrency,
    hotelSearchListDisplayCurrency: displayCurrency,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
    pricingAuditMeta,
    currencyRate,
    usdToIls,
    ilsToUsd,
  });

  if (vm.pricingStatus !== "ok" || vm.finalPrice == null) {
    return {
      vm,
      priceWithCommission: null,
      displayLine: null,
      pricingStatus: vm.pricingStatus,
    };
  }

  const displayLine =
    formatHotelSearchAmountForDisplay(vm.finalPrice, vm.displayCurrency) ??
    formatResolvedAgentHotelPriceLine(vm);

  return {
    vm,
    supplierNet: vm.basePrice,
    hrCommissionPercent: vm.commissionPercent,
    hrCommission: vm.commissionAmount,
    priceWithCommission: vm.finalPrice,
    displayLine,
    pricingStatus: "ok",
  };
}

/**
 * Order draft ourPrice — agent HR SSOT when enabled, else legacy package ourPrice.
 * @param {object|null|undefined} pkg
 * @param {object|null|undefined} profilePriceContext
 * @param {object|null|undefined} [agentOurPrice] — from mergeHotelOrderDraftBookingSsot when already resolved
 * @returns {object|null}
 */
export function pickAgentHotelPackageOrderOurPrice(
  pkg,
  profilePriceContext,
  agentOurPrice = null
) {
  if (agentOurPrice && typeof agentOurPrice === "object") {
    return agentOurPrice;
  }
  if (!isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    return null;
  }
  const cf = resolveAgentHotelCustomerFacingPriceIls({
    pkg,
    hotelInfo: profilePriceContext?.hotelInfo ?? null,
    profilePriceContext,
    pricingModel: "hotel_profile:agent_hr_order_our_price",
  });
  return cf.pricingStatus === "ok" ? cf.ourPrice : null;
}

/** Room lock / order draft — agent HR ourPrice when enabled, else BE package ourPrice. */
export function resolveHotelPackageOrderOurPrice(
  pkg,
  profilePriceContext,
  agentOurPrice = null
) {
  const agentPrice = pickAgentHotelPackageOrderOurPrice(
    pkg,
    profilePriceContext,
    agentOurPrice
  );
  if (agentPrice) return agentPrice;
  return pickHotelPackageOrderOurPrice(pkg);
}
